/**
 * QuestionnaireDetailPage — la pagina di un questionario (rotta /questionari/:id): le statistiche
 * e gli iscritti, con l'esportazione per ricontattarli. Raggiungibile dall'elenco dei questionari.
 *
 * Doc canonico: docs/01-ARCHITECTURE/PLATFORM-CRM-QUESTIONARI-MODEL.md
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, IconButton, Snackbar, Stack, Tab, Tabs, Tooltip, Typography,
} from '@mui/material';
import { ArrowBack, EditOutlined, Link as LinkIcon, Refresh } from '@mui/icons-material';
import { contaNeiConti } from '@shared/questionari';
import { tokens } from '../theme';
import { SoftChip } from '../components/SoftChip';
import { LeadDetailDrawer } from '../components/platform-crm/LeadDetailDrawer';
import { QuestionnaireBuilderDialog } from '../components/platform-crm/QuestionnaireBuilderDialog';
import { QuestionnaireLinksDialog } from '../components/platform-crm/QuestionnaireLinksDialog';
import { SchedaIscritti } from '../components/platform-crm/questionario/SchedaIscritti';
import { SchedaStatistiche } from '../components/platform-crm/questionario/SchedaStatistiche';
import { useDatiQuestionario } from '../components/platform-crm/questionario/useDatiQuestionario';
import { distinctPartnerSegnalatori } from '../services/platformCrmService';
import type { PlatformLead, PlatformQuestionnaire } from '../types/platformCrm';

type Scheda = 'statistiche' | 'iscritti';

export const QuestionnaireDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dati = useDatiQuestionario(id);
  const [scheda, setScheda] = useState<Scheda>('statistiche');
  const [leadAperto, setLeadAperto] = useState<PlatformLead | null>(null);
  const [costruttore, setCostruttore] = useState(false);
  const [link, setLink] = useState<PlatformQuestionnaire | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const showSnack = (msg: string, severity: 'success' | 'error') => setSnack({ msg, severity });
  const leads = useMemo(() => Array.from(dati.lead.values()), [dati.lead]);
  const partnerOptions = useMemo(() => distinctPartnerSegnalatori(leads), [leads]);
  const questionari = useMemo(
    () => new Map(dati.questionario ? [[dati.questionario.id, dati.questionario]] : []),
    [dati.questionario],
  );
  const iscritti = dati.risposte.filter(contaNeiConti).length;
  // Il lead aperto si rilegge dalla mappa aggiornata dopo ogni salvataggio.
  const leadCorrente = leadAperto ? dati.lead.get(leadAperto.id) ?? leadAperto : null;

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={2}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Tooltip title="Torna ai questionari">
            <IconButton size="small" onClick={() => navigate('/questionari')}><ArrowBack fontSize="small" /></IconButton>
          </Tooltip>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: tokens.font.display, fontWeight: 600, fontSize: { xs: '1.45rem', md: '1.8rem' }, lineHeight: 1.1,
                letterSpacing: '-0.02em', color: tokens.color.graphite, overflowWrap: 'anywhere',
              }}
            >
              {dati.questionario?.titolo ?? 'Questionario'}
            </Typography>
            {dati.questionario && (
              <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                {dati.questionario.attivo ? <SoftChip label="Attivo" tone="success" /> : <SoftChip label="Disattivo" tone="default" outlined />}
                <SoftChip label={`${iscritti} ${iscritti === 1 ? 'iscritto' : 'iscritti'}`} tone="primary" />
              </Stack>
            )}
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Ricarica">
            <span><IconButton size="small" onClick={dati.ricarica} disabled={dati.caricamento}><Refresh fontSize="small" /></IconButton></span>
          </Tooltip>
          <Button variant="outlined" startIcon={<LinkIcon />} disabled={!dati.questionario} onClick={() => setLink(dati.questionario)}>
            Link
          </Button>
          <Button variant="contained" startIcon={<EditOutlined />} disabled={!dati.questionario} onClick={() => setCostruttore(true)}>
            Modifica
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ borderBottom: `1px solid ${tokens.color.line}`, mb: 2.5 }}>
        <Tabs value={scheda} onChange={(_, v) => setScheda(v)} sx={{ '& .MuiTab-root': { minHeight: 44, textTransform: 'none' } }}>
          <Tab value="statistiche" label="Statistiche" />
          <Tab value="iscritti" label={`Iscritti (${iscritti})`} />
        </Tabs>
      </Box>

      {dati.errore && <Alert severity="error" sx={{ mb: 2 }}>{dati.errore}</Alert>}
      {dati.caricamento && !dati.questionario ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : dati.questionario && (
        scheda === 'statistiche'
          ? <SchedaStatistiche dati={dati} />
          : <SchedaIscritti dati={dati} onApriLead={setLeadAperto} />
      )}

      <LeadDetailDrawer
        lead={leadCorrente}
        questionnaires={questionari}
        partnerOptions={partnerOptions}
        onClose={() => setLeadAperto(null)}
        onSaved={dati.ricarica}
        showSnack={showSnack}
      />
      <QuestionnaireBuilderDialog
        open={costruttore}
        questionnaire={dati.questionario}
        onClose={() => setCostruttore(false)}
        onSaved={() => { showSnack('Questionario aggiornato', 'success'); dati.ricarica(); }}
      />
      <QuestionnaireLinksDialog questionnaire={link} leads={leads} onClose={() => setLink(null)} showSnack={showSnack} />

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};
